"use client"

// src/stack.tsx
import { forwardRef, ui } from "@yamada-ui/core";
import {
  cx,
  getRef,
  getValidChildren,
  mergeRefs,
  replaceObject
} from "@yamada-ui/utils";
import {
  cloneElement,
  createRef,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var Stack = forwardRef(
  ({
    className,
    align: alignItems,
    children,
    direction: flexDirection = "column",
    divider,
    gap = "fallback(md, 1rem)",
    justify: justifyContent,
    separator = divider,
    wrap: flexWrap,
    ...rest
  }, ref) => {
    const isColumn = (value) => value === "column" || value === "column-reverse";
    const separatorCSS = useMemo(
      () => ({
        borderBottomWidth: replaceObject(
          flexDirection,
          (value) => isColumn(value) ? "1px" : 0
        ),
        borderLeftWidth: replaceObject(
          flexDirection,
          (value) => isColumn(value) ? 0 : "1px"
        ),
        h: replaceObject(
          flexDirection,
          (value) => isColumn(value) ? "fit-content" : "100%"
        ),
        w: replaceObject(
          flexDirection,
          (value) => isColumn(value) ? "100%" : "fit-content"
        )
      }),
      [flexDirection]
    );
    const validChildren = getValidChildren(children);
    const cloneChildren = separator ? validChildren.map((child, index) => {
      const key = typeof child.key !== "undefined" ? child.key : index;
      const cloneSeparator = cloneElement(separator, {
        __css: separatorCSS
      });
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        !!index ? cloneSeparator : null,
        child
      ] }, key);
    }) : validChildren;
    const css = useMemo(
      () => ({
        alignItems,
        display: "flex",
        flexDirection,
        flexWrap,
        gap,
        justifyContent
      }),
      [alignItems, flexDirection, flexWrap, gap, justifyContent]
    );
    return /* @__PURE__ */ jsx(
      ui.div,
      {
        ref,
        className: cx("ui-stack", className),
        __css: css,
        ...rest,
        children: cloneChildren
      }
    );
  }
);
Stack.displayName = "Stack";
Stack.__ui__ = "Stack";
var HStack = forwardRef(
  ({ className, ...rest }, ref) => /* @__PURE__ */ jsx(
    Stack,
    {
      ref,
      className: cx("ui-stack--horizontal", className),
      align: "center",
      direction: "row",
      ...rest
    }
  )
);
HStack.displayName = "HStack";
HStack.__ui__ = "HStack";
var VStack = forwardRef(
  ({ className, ...rest }, ref) => /* @__PURE__ */ jsx(
    Stack,
    {
      ref,
      className: cx("ui-stack--vertical", className),
      align: "stretch",
      direction: "column",
      w: "100%",
      ...rest
    }
  )
);
VStack.displayName = "VStack";
VStack.__ui__ = "VStack";
var ZStack = forwardRef(
  ({
    className,
    children,
    direction = "bottom",
    fit = true,
    gap = "fallback(md, 1rem)",
    reverse = false,
    startIndex = 0,
    ...rest
  }, ref) => {
    const refMap = useRef(/* @__PURE__ */ new Map());
    const [rect, setRect] = useState({
      height: 0,
      width: 0
    });
    const boxSize = {
      minHeight: `${rect.height}px`,
      minWidth: `${rect.width}px`
    };
    const css = {
      overflow: "hidden",
      position: "relative",
      vars: [{ name: "space", token: "spaces", value: gap }],
      ...fit ? boxSize : {}
    };
    const vertical = useCallback(
      (space) => {
        if (direction.includes("top")) {
          return { [!reverse ? "bottom" : "top"]: space };
        } else if (direction.includes("bottom")) {
          return { [!reverse ? "top" : "bottom"]: space };
        } else {
          return { [!reverse ? "top" : "bottom"]: 0 };
        }
      },
      [direction, reverse]
    );
    const horizontal = useCallback(
      (space) => {
        if (direction.includes("left")) {
          return { [!reverse ? "right" : "left"]: space };
        } else if (direction.includes("right")) {
          return { [!reverse ? "left" : "right"]: space };
        } else {
          return { [!reverse ? "left" : "right"]: 0 };
        }
      },
      [direction, reverse]
    );
    const cloneChildren = useMemo(() => {
      const validChildren = getValidChildren(children);
      const clonedChildren = validChildren.map((child, index) => {
        var _a;
        const ref2 = createRef();
        refMap.current.set(index, ref2);
        const key = (_a = child.key) != null ? _a : index;
        const zIndex = startIndex + index;
        const space = `calc($space * ${index})`;
        let css2 = {};
        css2 = {
          ...css2,
          position: "absolute",
          zIndex
        };
        css2 = { ...css2, ...vertical(space) };
        css2 = { ...css2, ...horizontal(space) };
        const props = {
          ...child.props,
          ref: mergeRefs(getRef(child), ref2),
          __css: css2
        };
        const clonedChild = cloneElement(child, props);
        return /* @__PURE__ */ jsx(Fragment, { children: clonedChild }, key);
      });
      return clonedChildren;
    }, [children, startIndex, vertical, horizontal]);
    useEffect(() => {
      var _a, _b;
      if (!fit) return;
      let isNegativeLeft = direction.includes("left");
      let isNegativeTop = direction.includes("top");
      let width = 0;
      let height = 0;
      if (reverse) {
        isNegativeLeft = !isNegativeLeft;
        isNegativeTop = !isNegativeTop;
      }
      for (const ref2 of refMap.current.values()) {
        if (!ref2.current) continue;
        let { offsetHeight, offsetLeft, offsetParent, offsetTop, offsetWidth } = ref2.current;
        if (!offsetParent && process.env.NODE_ENV !== "test") continue;
        if (isNegativeLeft) {
          const offsetParentWidth = (_a = offsetParent == null ? void 0 : offsetParent.offsetWidth) != null ? _a : 0;
          offsetLeft = offsetParentWidth - offsetLeft - offsetWidth;
        }
        if (isNegativeTop) {
          const offsetParentHeight = (_b = offsetParent == null ? void 0 : offsetParent.offsetHeight) != null ? _b : 0;
          offsetTop = offsetParentHeight - offsetTop - offsetHeight;
        }
        offsetWidth += offsetLeft;
        offsetHeight += offsetTop;
        if (offsetWidth > width) width = offsetWidth;
        if (offsetHeight > height) height = offsetHeight;
      }
      setRect({ height, width });
    }, [cloneChildren, direction, reverse, fit]);
    return /* @__PURE__ */ jsx(
      ui.div,
      {
        ref,
        className: cx("ui-stack--depth", className),
        __css: css,
        ...rest,
        children: cloneChildren
      }
    );
  }
);
ZStack.displayName = "ZStack";
ZStack.__ui__ = "ZStack";

export {
  Stack,
  HStack,
  VStack,
  ZStack
};
//# sourceMappingURL=chunk-KNFS6H5K.mjs.map